import { useEffect, useRef, useCallback, useState } from 'react';
import {
  getOrdersAblyClient,
  getOrdersChannel,
} from '@/utils/ably';
import type { Order } from '@/utils/supabase';
import type { Realtime } from 'ably';

/**
 * Token 1 — Orders Channel (Customer + Staff + Admin)
 * Customer subscribes to order updates for their orders.
 * Staff subscribes to new orders and assignment updates.
 * Admin subscribes to all orders.
 */

export function useAblyOrders(userId: string, userRole: string) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const ordersRef = useRef<Order[]>([]);
  const clientRef = useRef<Realtime | null>(null);
  const [ready, setReady] = useState(false);

  // Sync ref with state
  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  // Initialize orders Ably client async
  useEffect(() => {
    let cancelled = false;
    getOrdersAblyClient().then((c) => {
      if (cancelled) return;
      clientRef.current = c;
      setReady(!!c);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Subscribe to orders channel ─────────────────────────────────────
  const subscribeToOrders = useCallback(() => {
    const client = clientRef.current;
    if (!client || !ready || !userId || userRole === 'customer') return;

    const channel = getOrdersChannel(client);

    channel.on('attached', () => {
      if (import.meta.env.DEV) console.log('[Ably-Orders] Attached to orders channel');
      setIsConnected(true);
    });

    channel.subscribe('order_update', (msg) => {
      const rawPayload = msg.data as Order & { orderId?: string };
      const payload = { ...rawPayload, id: rawPayload.id || rawPayload.orderId } as Order;
      if (!payload.id) return;
      if (import.meta.env.DEV) console.log('[Ably-Orders] Received order update:', payload.id);

      setOrders((prev) => {
        // Filter based on role
        if (
          userRole === 'staff' &&
          payload.assigned_staff_id !== userId &&
          payload.status !== 'pending'
        ) {
          return prev;
        }

        const exists = prev.find((o) => o.id === payload.id);
        if (exists) {
          return prev.map((o) => (o.id === payload.id ? payload : o));
        }
        return [...prev, payload];
      });
    });

    channel.subscribe('new_order', (msg) => {
      const rawPayload = msg.data as Order & { orderId?: string };
      const payload = { ...rawPayload, id: rawPayload.id || rawPayload.orderId } as Order;
      if (!payload.id) return;
      if (import.meta.env.DEV) console.log('[Ably-Orders] New order received:', payload.id);

      if (userRole === 'staff' && payload.status === 'pending') {
        setOrders((prev) => {
          if (prev.find((o) => o.id === payload.id)) return prev;
          return [...prev, payload];
        });
      }
    });

    return () => {
      channel.unsubscribe();
      channel.detach();
    };
  }, [ready, userId, userRole]);


  // ─── Auto-subscribe when user changes ────────────────────────────────
  useEffect(() => {
    if (!userId || !ready) return;

    const unsubscribe = subscribeToOrders();

    return () => {
      unsubscribe?.();
    };
  }, [userId, userRole, ready, subscribeToOrders]);

  return {
    orders,
    isConnected,
    subscribeToOrders,
  };
}
